import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SacredGeometry from '../components/SacredGeometry';
import AnimatedBackground from '../components/AnimatedBackground';

const Services = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const services = [
    {
      title: 'Constellations familiales systémiques',
      description: 'Les constellations familiales systémiques permettent de mettre en lumière certaines dynamiques invisibles, loyautés inconscientes ou blocages hérités du système familial. Cet accompagnement aide à retrouver plus de clarté, de paix et une place plus juste dans sa propre vie. Séances individuelles ou en groupe sur demande.',
      image: '/images/constellations.jpg',
      gradient: 'from-purple-600 to-violet-700',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
    },
    {
      title: 'Communication animale',
      description: 'La communication animale est une approche sensible et intuitive permettant d\'entrer en lien avec l\'animal pour mieux percevoir ses ressentis, ses besoins ou certains messages subtils. Elle favorise une compréhension plus profonde et une relation plus harmonieuse entre l\'humain et l\'animal.',
      image: '/images/communication-animale.jpg',
      gradient: 'from-lavender-500 to-purple-600',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
      ),
    },
    {
      title: 'Thérapie Quantique',
      description: 'La thérapie quantique propose un travail énergétique visant à soutenir les processus de réalignement intérieur. Elle accompagne la libération de certaines tensions, favorise une meilleure circulation des énergies et invite à un mieux-être global.',
      image: '/images/therapie-quantique.jpg',
      gradient: 'from-violet-600 to-purple-700',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="1.5" fill="currentColor" />
          <ellipse cx="12" cy="12" rx="9" ry="3.5" strokeWidth={1.5} />
          <ellipse cx="12" cy="12" rx="9" ry="3.5" strokeWidth={1.5} transform="rotate(60 12 12)" />
          <ellipse cx="12" cy="12" rx="9" ry="3.5" strokeWidth={1.5} transform="rotate(120 12 12)" />
        </svg>
      ),
    },
    {
      title: 'Clairconnaissance',
      description: 'La clairconnaissance est une faculté intuitive qui permet de recevoir des compréhensions, des ressentis ou des informations de manière subtile et spontanée. Cet accompagnement peut aider à éclairer certaines situations, à ouvrir de nouvelles perspectives et à avancer avec plus de justesse.',
      image: '/images/clairconnaissance.jpg',
      gradient: 'from-purple-500 to-lavender-600',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
    },
    {
      title: 'Radiesthésie',
      description: 'La radiesthésie permet de capter, ressentir et interpréter certaines vibrations ou informations énergétiques. Elle peut être utilisée comme un outil d\'exploration, d\'harmonisation et de rééquilibrage dans une démarche sensible et intuitive.',
      image: '/images/radiesthesie.jpg',
      gradient: 'from-lavender-600 to-violet-600',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <line x1="12" y1="2" x2="12" y2="15" strokeWidth={1.5} strokeLinecap="round" />
          <circle cx="12" cy="2" r="1" fill="currentColor" />
          <path d="M12 15 L9 20 L15 20 Z" fill="currentColor" stroke="currentColor" strokeWidth={1.5} strokeLinejoin="round" />
          <path d="M6 18 Q12 16 18 18" strokeWidth={1} strokeDasharray="2,2" opacity="0.5" />
        </svg>
      ),
    },
  ];

  return (
    <div className="w-full">
      {/* HERO SECTION */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        {/* Multi-layer background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-purple-700 to-lavender-600"></div>
        <div className="absolute inset-0 bg-gradient-to-tr from-violet-800/50 via-transparent to-purple-600/30"></div>
        
        {/* Animated background elements */}
        <AnimatedBackground opacity={0.6} />
        
        {/* Sacred geometry background */}
        <SacredGeometry opacity={0.06} />
        
        {/* Content */}
        <div className="relative z-10 text-center px-6 py-20 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-bold text-white mb-8 animate-fade-in text-glow">
            Services
          </h1>
          
          <p className="text-lg md:text-xl lg:text-2xl text-purple-100 leading-relaxed animate-fade-in-up font-light max-w-3xl mx-auto" style={{ animationDelay: '100ms' }}>
            Des accompagnements subtils et profonds pour mieux comprendre, apaiser et réaligner ce qui demande à l'être.
          </p>
        </div>
      </section>

      {/* SERVICES SECTION */}
      <section className="relative py-20 md:py-32 px-6 bg-gradient-to-b from-white via-purple-50/10 to-white">
        {/* Subtle background geometry */}
        <div className="absolute inset-0 overflow-hidden">
          <SacredGeometry opacity={0.015} />
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto">
          {/* Services */}
          <div className="space-y-20 md:space-y-28">
            {services.map((service, index) => (
              <div
                key={index}
                className="group animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Decorative line */}
                <div className="w-16 h-0.5 bg-gradient-to-r from-purple-400 to-lavender-500 mb-8 group-hover:w-24 transition-all duration-700"></div>
                
                {/* Icon */}
                <div className={`w-16 h-16 bg-gradient-to-br ${service.gradient} rounded-2xl flex items-center justify-center text-white shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-500 mb-6`}>
                  {service.icon}
                </div>
                
                {/* Content */}
                <div className="space-y-8">
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-semibold text-purple-900 leading-tight group-hover:text-purple-700 transition-colors duration-500">
                    {service.title}
                  </h2>
                  
                  <p className="text-gray-600 leading-relaxed text-base md:text-lg lg:text-xl max-w-4xl">
                    {service.description}
                  </p>
                  
                  {/* Image */}
                  <div className="mt-8">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full max-w-2xl h-64 md:h-80 object-cover rounded-2xl shadow-soft border border-purple-100/50 group-hover:shadow-glow transition-all duration-500"
                    />
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-4 mt-8">
                    <Link
                      to="/tarifs"
                      className="inline-block px-8 py-3 bg-white border-2 border-purple-300 text-purple-700 rounded-full hover:bg-purple-50 hover:border-purple-400 transition-all duration-300 font-medium text-base"
                    >
                      Voir les tarifs
                    </Link>
                    <Link
                      to="/contact"
                      className="inline-block px-8 py-3 bg-gradient-to-r from-purple-600 to-lavender-600 text-white rounded-full hover:shadow-glow transition-all duration-300 font-medium text-base transform hover:scale-105"
                    >
                      Prendre rendez-vous
                    </Link>
                  </div>
                </div>
                
                {/* Subtle separator for all but last item */}
                {index < services.length - 1 && (
                  <div className="mt-16 md:mt-20 w-full h-px bg-gradient-to-r from-transparent via-purple-200/50 to-transparent"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DÉROULEMENT D'UNE SÉANCE SECTION */}
      <section className="relative py-20 md:py-32 px-6 bg-gradient-to-b from-white via-purple-50/30 to-white">
        {/* Subtle background geometry */}
        <div className="absolute inset-0 overflow-hidden">
          <SacredGeometry opacity={0.02} />
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto">
          {/* Title */}
          <div className="text-center mb-16 md:mb-20">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-semibold text-purple-900 mb-6">
              Déroulement d'une séance
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-purple-400 to-lavender-500 mx-auto rounded-full"></div>
          </div>
          
          {/* Steps */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
            {/* Step 1 */}
            <div className="group text-center animate-fade-in-up" style={{ animationDelay: '0ms' }}>
              <div className="relative mb-6 mx-auto w-20 h-20 bg-gradient-to-br from-purple-600 to-violet-700 rounded-full flex items-center justify-center text-white shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-500">
                <span className="text-3xl font-serif font-bold">1</span>
                <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 rounded-full transition-opacity duration-500"></div>
              </div>
              <h3 className="text-xl font-serif font-semibold text-purple-900 mb-3">Accueil</h3>
              <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                Moment d'écoute et d'échange pour comprendre votre demande et vos besoins.
              </p>
            </div>
            
            {/* Step 2 */}
            <div className="group text-center animate-fade-in-up" style={{ animationDelay: '100ms' }}>
              <div className="relative mb-6 mx-auto w-20 h-20 bg-gradient-to-br from-lavender-500 to-purple-600 rounded-full flex items-center justify-center text-white shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-500">
                <span className="text-3xl font-serif font-bold">2</span>
                <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 rounded-full transition-opacity duration-500"></div>
              </div>
              <h3 className="text-xl font-serif font-semibold text-purple-900 mb-3">Travail</h3>
              <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                Accompagnement adapté selon la pratique choisie, dans le respect et la bienveillance.
              </p>
            </div>
            
            {/* Step 3 */}
            <div className="group text-center animate-fade-in-up" style={{ animationDelay: '200ms' }}>
              <div className="relative mb-6 mx-auto w-20 h-20 bg-gradient-to-br from-violet-600 to-purple-700 rounded-full flex items-center justify-center text-white shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-500">
                <span className="text-3xl font-serif font-bold">3</span>
                <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 rounded-full transition-opacity duration-500"></div>
              </div>
              <h3 className="text-xl font-serif font-semibold text-purple-900 mb-3">Intégration</h3>
              <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                Temps d'échange pour intégrer ce qui s'est révélé pendant la séance.
              </p>
            </div>
            
            {/* Step 4 */}
            <div className="group text-center animate-fade-in-up" style={{ animationDelay: '300ms' }}>
              <div className="relative mb-6 mx-auto w-20 h-20 bg-gradient-to-br from-purple-500 to-lavender-600 rounded-full flex items-center justify-center text-white shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-500">
                <span className="text-3xl font-serif font-bold">4</span>
                <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 rounded-full transition-opacity duration-500"></div>
              </div>
              <h3 className="text-xl font-serif font-semibold text-purple-900 mb-3">Suivi</h3>
              <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                Possibilité de poursuivre l'accompagnement selon vos besoins et votre rythme.
              </p>
            </div>
          </div>
          
          {/* Additional info */}
          <div className="mt-16 text-center max-w-3xl mx-auto">
            <p className="text-gray-600 text-base md:text-lg leading-relaxed italic">
              Chaque séance dure entre 60 et 90 minutes. L'espace est pensé pour être accueillant, calme et propice à l'introspection.
            </p>
          </div>
        </div>
      </section>

      {/* QUOTE SECTION */}
      <section className="relative py-32 md:py-40 px-6 overflow-hidden">
        {/* Multi-layer background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-800 via-purple-600 to-lavender-500"></div>
        <div className="absolute inset-0 bg-gradient-to-tl from-violet-700/40 via-transparent to-purple-700/30"></div>
        
        {/* Animated background elements */}
        <AnimatedBackground opacity={0.5} />
        
        {/* Sacred geometry */}
        <SacredGeometry opacity={0.05} />
        
        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-medium text-white leading-relaxed text-glow italic px-6">
            "Écouter l'invisible, c'est parfois commencer à transformer le visible."
          </h2>
        </div>
      </section>

      {/* FINAL SECTION */}
      <section className="relative py-20 md:py-24 px-6 bg-gradient-to-b from-white to-purple-50/20">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-gray-600 text-lg md:text-xl mb-10 max-w-2xl mx-auto leading-relaxed">
            Chaque accompagnement est proposé avec présence, écoute et respect de votre rythme.
          </p>
          <Link
            to="/contact"
            className="inline-block px-12 py-4 bg-gradient-to-r from-purple-600 to-lavender-600 text-white rounded-full hover:shadow-glow-lg transition-all duration-300 font-medium text-lg transform hover:scale-105"
          >
            Me contacter
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
